import React, { useMemo, useEffect, useState } from "react";
import { connect } from "react-redux";
import Select from "react-select";
import translate from "../../../i18n/translate";
import { useIntl } from "react-intl";
import ApiRequest from "../../../actions/ApiRequest";

const LoadStepper = ({
  driverType,
  setEngineSize,
  setTon,
  setPassenger,
  passenger,
  ton,
  engineSize,
  handleNext,
  handleBack,
  isDarktheme,
}) => {
  const intl = useIntl();
  const [engineSizes, setEngineSizes] = useState([]);
  const [loadingEngineSizes, setLoadingEngineSizes] = useState(false);
  
  // Default/fallback engine sizes if API fails or returns no data
  const defaultEngineSizes = useMemo(() => [
    { value: "up to 1000cc", label: "up to 1000cc" },
    { value: "1001cc to 1300cc", label: "1001cc to 1300cc" },
    { value: "1301cc to 1600cc", label: "1301cc to 1600cc" },
    { value: "1601cc to 2000cc", label: "1601cc to 2000cc" },
    { value: "2001cc to 2500cc", label: "2001cc to 2500cc" },
    { value: "2501cc and above", label: "2501cc and above" },
  ], []);

  // Fetch engine sizes from backend API
  useEffect(() => {
    const fetchEngineSizes = async () => {
      if (driverType?.value === "private") {
        setLoadingEngineSizes(true);
        try {
          const response = await ApiRequest.post('get_insurance_engine_sizes');
          console.log('Engine sizes API response:', response);
          console.log('Response data:', response.data);
          console.log('Engine sizes array:', response.data?.engine_sizes);
          
          if (response.data?.status && response.data?.engine_sizes && response.data.engine_sizes.length > 0) {
            // Convert API response to react-select format
            const formattedSizes = response.data.engine_sizes.map(size => ({
              value: size,
              label: size // Display the value from database as-is
            }));
            console.log('Formatted engine sizes from API:', formattedSizes);
            setEngineSizes(formattedSizes);
          } else {
            console.warn('No engine sizes in response, using default values');
            setEngineSizes(defaultEngineSizes);
          }
        } catch (error) {
          console.error("Error fetching engine sizes:", error);
          // Fallback to default engine sizes if API fails
          console.log('Using default engine sizes due to API error');
          setEngineSizes(defaultEngineSizes);
        } finally {
          setLoadingEngineSizes(false);
        }
      }
    };
    
    fetchEngineSizes();
  }, [driverType?.value, defaultEngineSizes]);

  const tons = useMemo(() => [
    { value: "below 1 ton", label: intl.formatMessage({ id: "below 1 ton" }) },
    {
      value: "from 1 ton to 1.6 to ton",
      label: intl.formatMessage({ id: "from 1 ton to 1.6 to ton" }),
    },
    {
      value: "more than 4 tons",
      label: intl.formatMessage({ id: "more than 4 tons" }),
    },
  ], [intl]);

  const passengers = useMemo(() => [
    {
      value: "passengers_4_to_6",
      label: intl.formatMessage({ id: "4 to 6 passengers" }),
    },
    { value: "passengers_7", label: intl.formatMessage({ id: "7 passengers" }) },
    { value: "passengers_8", label: intl.formatMessage({ id: "8 passengers" }) },
  ], [intl]);

  const disableBtn = () => {
    if (driverType.value === "private" && !engineSize) {
      return true;
    }
    if (driverType.value === "commercial" && !ton) {
      return true;
    }
    if (
      (driverType.value === "taxi_1_driver" ||
        driverType.value === "taxi_2_drivers" ||
        driverType.value === "taxi_multiple_drivers") &&
      !passenger
    ) {
      return true;
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (driverType.value === "private" && !engineSize) {
      return;
    }
    if (driverType.value === "commercial" && !ton) {
      return;
    }
    if (
      (driverType.value === "taxi_1_driver" ||
        driverType.value === "taxi_2_drivers" ||
        driverType.value === "taxi_multiple_drivers") &&
      !passenger
    ) {
      return;
    }
    handleNext(e);
  };

  return (
    <form className="vehicle-stepper" onSubmit={onSubmit}>
      {driverType.value === "private" && (
        <div className="select-input">
          <label
            style={
              !isDarktheme
                ? { marginBottom: 6 }
                : { marginBottom: 6, color: "white" }
            }
          >
            <span>
              {translate("Engine Size")} <i className="asterisk">*</i>
            </span>
          </label>
          <Select
            name="enigne_size"
            placeholder={loadingEngineSizes ? translate("Loading...") : intl.formatMessage({ id: "Engine Size" })}
            options={engineSizes}
            value={engineSize}
            required={true}
            isLoading={loadingEngineSizes}
            isDisabled={loadingEngineSizes}
            noOptionsMessage={() => "No engine sizes available"}
            onChange={(v) => {
              console.log('Engine size selected:', v);
              setEngineSize(v);
              setTon(null);
              setPassenger(null);
            }}
            styles={{
              control: (baseStyles, state) => ({
                ...baseStyles,
                minHeight: '42px',
                borderColor: state.isFocused ? '#667eea' : '#cbd5e0',
                boxShadow: state.isFocused ? '0 0 0 1px #667eea' : 'none',
                '&:hover': {
                  borderColor: '#667eea'
                },
                cursor: loadingEngineSizes ? 'not-allowed' : 'pointer',
                backgroundColor: loadingEngineSizes ? '#f5f5f5' : 'white'
              }),
              menu: (baseStyles) => ({
                ...baseStyles,
                zIndex: 9999
              }),
              menuPortal: (baseStyles) => ({
                ...baseStyles,
                zIndex: 9999
              })
            }}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>
      )}
      {driverType.value === "commercial" && (
        <div className="select-input">
          <label
            style={
              !isDarktheme
                ? { marginBottom: 6 }
                : { marginBottom: 6, color: "white" }
            }
          >
            <span>
              {translate("Select Vehicle Weight")}
              <i className="asterisk">*</i>
            </span>
          </label>
          <Select
            name="tons"
            placeholder={intl.formatMessage({ id: "Select Vehicle Weight" })}
            options={tons}
            value={ton}
            required={true}
            onChange={(v) => {
              setTon(v);
              setEngineSize(null);
              setPassenger(null);
            }}
          />
        </div>
      )}
      {(driverType.value === "taxi_1_driver" ||
        driverType.value === "taxi_2_drivers" ||
        driverType.value === "taxi_multiple_drivers") && (
        <div className="select-input">
          <label
            style={
              !isDarktheme
                ? { marginBottom: 6 }
                : { marginBottom: 6, color: "white" }
            }
          >
            <span>
              {translate("Select Vehicle Passengers")}
              <i className="asterisk">*</i>
            </span>
          </label>
          <Select
            name="passengers"
            placeholder={intl.formatMessage({
              id: "Select Vehicle Passengers",
            })}
            options={passengers}
            value={passenger}
            required={true}
            onChange={(v) => {
              setPassenger(v);
              setTon(null);
              setEngineSize(null);
            }}
          />
        </div>
      )}
      <div
        className="d-flex justify-content-between align-items-center mt-4"
        style={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          padding: "0 15px",
        }}
      >
        <button
          className="btn btn-primary back"
          onClick={handleBack}
          style={{ position: "static" }}
        >
          {translate("Back")}
        </button>
        <button
          onSubmit={onSubmit}
          type="submit"
          className="btn btn-primary d-flex align-items-center justify-content-center"
          style={{ position: "static" }}
          disabled={disableBtn()}
        >
          <div>{intl.formatMessage({ id: "next" })}</div>
        </button>
      </div>
    </form>
  );
};

const mapStateToProps = ({
  insurances: { insuranceCalculationsLoading },
  darkTheme: { isDarktheme },
}) => {
  return {
    insuranceCalculationsLoading,
    isDarktheme,
  };
};

export default connect(mapStateToProps, null)(LoadStepper);
